import type { Identifier } from "ra-core";

import type {
  AppRole,
  InternalNoticeConfig,
  InternalNoticeUsage,
  InternalNoticeUser,
  LeadTakeRecord,
  MyInternalNoticePrefs,
} from "../../types";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EN (TASK-091): Supabase-side internal notices methods. Every write goes through the
 * TASK-087 RPCs or the direccion-only RLS policies, so the browser never decides who may switch
 * what. The roster phone is read ONLY to learn whether a user has one (`hasPhone`): it is dropped
 * here and never reaches a component. The test notice goes through the `internal_notices` edge
 * function in `test` mode, which alone holds the WhatsApp secrets.
 */
const REGISTERED_PHONE_PREFIX = "+52";

const fail = (error: { message: string } | null, fallback: string) => {
  if (error) throw new Error(error.message || fallback);
};

type OrgChartRow = {
  id: Identifier;
  first_name: string;
  last_name: string;
  role: AppRole;
  projects: { name: string }[] | null;
  phone: string | null;
};

type PrefsRow = {
  sales_id: Identifier;
  internal_notices_enabled: boolean;
  receives_general_summary: boolean;
};

type ConfigRow = {
  grouping_window_minutes: number;
  summaries_on_weekends: boolean;
  holiday_dates: string[] | null;
};

type UsageRow = {
  month_start: string;
  month_end: string;
  service_messages: number;
  monthly_free_quota: number;
  paid_templates: number;
};

type TakeRow = {
  taken_at: string;
  sales: { first_name: string; last_name: string } | null;
};

export const createSupabaseInternalNoticesMethods = () => ({
  /** Active users with their switches (direccion only: RLS shows the other rows to nobody else). */
  async getInternalNoticeUsers(): Promise<InternalNoticeUser[]> {
    const client = getSupabaseClient();
    const [people, prefs] = await Promise.all([
      client
        .from("org_chart_people")
        .select("id, first_name, last_name, role, projects, phone")
        .order("first_name", { ascending: true }),
      client
        .from("internal_notice_prefs")
        .select("sales_id, internal_notices_enabled, receives_general_summary"),
    ]);
    fail(people.error, "No se pudo leer a las personas del equipo");
    fail(prefs.error, "No se pudieron leer los avisos internos");
    const prefsBySales = new Map(
      ((prefs.data ?? []) as PrefsRow[]).map(
        (row) => [String(row.sales_id), row] as const,
      ),
    );
    return ((people.data ?? []) as OrgChartRow[]).map((person) => {
      const pref = prefsBySales.get(String(person.id));
      return {
        salesId: person.id,
        fullName: `${person.first_name} ${person.last_name}`.trim(),
        role: person.role,
        projects: (person.projects ?? []).map((project) => project.name),
        hasPhone:
          typeof person.phone === "string" &&
          person.phone.startsWith(REGISTERED_PHONE_PREFIX),
        enabled: pref?.internal_notices_enabled === true,
        receivesGeneralSummary: pref?.receives_general_summary === true,
      };
    });
  },

  /** Switches the module for one user (`set_internal_notices_enabled`, direccion only). */
  async setInternalNoticesEnabled(
    salesId: Identifier,
    enabled: boolean,
  ): Promise<void> {
    const { error } = await getSupabaseClient().rpc(
      "set_internal_notices_enabled",
      { p_sales_id: salesId, p_enabled: enabled },
    );
    fail(error, "No se pudo cambiar el aviso interno");
  },

  /** Flags a user as receiving the general summary (`internal_notice_prefs`, direccion RLS). */
  async setInternalNoticeGeneralSummary(
    salesId: Identifier,
    value: boolean,
  ): Promise<void> {
    const { error } = await getSupabaseClient()
      .from("internal_notice_prefs")
      .upsert(
        { sales_id: salesId, receives_general_summary: value },
        { onConflict: "sales_id" },
      );
    fail(error, "No se pudo cambiar el resumen general");
  },

  /** The ALERTS number's counter for the month `anchor` falls in: ONE aggregate RPC. */
  async getInternalNoticeUsage(anchor: string): Promise<InternalNoticeUsage> {
    const { data, error } = await getSupabaseClient().rpc(
      "internal_notice_usage",
      { p_now: anchor },
    );
    fail(error, "No se pudo leer el uso de los avisos internos");
    const row = ((data ?? []) as UsageRow[])[0];
    if (!row) throw new Error("No se pudo leer el uso de los avisos internos");
    return {
      monthStart: row.month_start,
      monthEnd: row.month_end,
      serviceMessages: row.service_messages,
      monthlyFreeQuota: row.monthly_free_quota,
      paidTemplates: row.paid_templates,
    };
  },

  /** The data-valued config (grouping window, weekend summaries, holidays), direccion only. */
  async getInternalNoticeConfig(): Promise<InternalNoticeConfig> {
    const { data, error } = await getSupabaseClient()
      .from("internal_notice_config")
      .select("grouping_window_minutes, summaries_on_weekends, holiday_dates")
      .eq("id", 1)
      .maybeSingle();
    fail(error, "No se pudo leer la configuracion de avisos internos");
    const row = data as ConfigRow | null;
    return {
      groupingWindowMinutes: row?.grouping_window_minutes ?? 5,
      summariesOnWeekends: row?.summaries_on_weekends ?? false,
      holidayDates: [...(row?.holiday_dates ?? [])].sort(),
    };
  },

  /** Saves the config singleton (RLS: direccion only). */
  async saveInternalNoticeConfig(config: InternalNoticeConfig): Promise<void> {
    const { error } = await getSupabaseClient()
      .from("internal_notice_config")
      .upsert(
        {
          id: 1,
          grouping_window_minutes: config.groupingWindowMinutes,
          summaries_on_weekends: config.summariesOnWeekends,
          holiday_dates: config.holidayDates,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "id" },
      );
    fail(error, "No se pudo guardar la configuracion de avisos internos");
  },

  /** The caller's own preferences; a user WITHOUT a row has the module OFF. */
  async getMyInternalNoticePrefs(
    salesId: Identifier,
  ): Promise<MyInternalNoticePrefs> {
    const { data, error } = await getSupabaseClient()
      .from("internal_notice_prefs")
      .select("internal_notices_enabled, summaries_enabled")
      .eq("sales_id", salesId)
      .maybeSingle();
    fail(error, "No se pudieron leer tus avisos internos");
    const row = data as {
      internal_notices_enabled: boolean;
      summaries_enabled: boolean;
    } | null;
    return {
      moduleEnabled: row?.internal_notices_enabled === true,
      summariesEnabled: row?.summaries_enabled ?? true,
    };
  },

  /** The caller switches her OWN summaries (`set_my_internal_summaries`). */
  async setMyInternalSummaries(enabled: boolean): Promise<void> {
    const { error } = await getSupabaseClient().rpc(
      "set_my_internal_summaries",
      { p_enabled: enabled },
    );
    fail(error, "No se pudo cambiar tu preferencia de resumenes");
  },

  /**
   * Sends one test notice to one user through `internal_notices` in `test` mode (direccion
   * session). Any failure throws a GENERIC error: no secret, phone or provider text is surfaced.
   */
  async sendInternalNoticeTest(salesId: Identifier): Promise<void> {
    const { data, error } = await getSupabaseClient().functions.invoke<{
      sent?: boolean;
    }>("internal_notices", { body: { mode: "test", sales_id: salesId } });
    if (error || data?.sent !== true) {
      throw new Error("internal_notice_test_failed");
    }
  },

  /** The newest take of a lead (who and when), or `null`; RLS follows the lead's visibility. */
  async getLeadTakeRecord(dealId: Identifier): Promise<LeadTakeRecord | null> {
    const { data, error } = await getSupabaseClient()
      .from("lead_take_records")
      .select("taken_at, sales:sales_id(first_name, last_name)")
      .eq("deal_id", dealId)
      .order("taken_at", { ascending: false })
      .limit(1);
    fail(error, "No se pudo leer quien tomo el lead");
    const row = ((data ?? []) as unknown as TakeRow[])[0];
    if (!row) return null;
    return {
      takenAt: row.taken_at,
      takerName: row.sales
        ? `${row.sales.first_name} ${row.sales.last_name}`.trim()
        : "",
    };
  },
});
